import { Box, Typography } from "@mui/material";
import { InfoSection } from "../../components/common/infoSection";
import { useReferenceLists } from "../../context/GlobalGetLists";
import type { Contact, Sponsor } from "../../types/deal";
import { useMemo } from "react";

interface ContactInfoSectionProps {
  contact: Contact;
  isEdit: boolean;
  isDealPage: boolean;
}

export const ContactDetail = ({
  contact,
  isEdit,
  isDealPage,
}: ContactInfoSectionProps) => {
  const { sponsors, deals, sources } = useReferenceLists();

  const resolvedLinks = useMemo(() => {
    const links = contact.links ?? [];
    const unique = (ids: number[]) => Array.from(new Set(ids));
    const validIds = (ids: Array<number | null | undefined>) =>
      ids.filter((v): v is number => typeof v === "number" && v > 0);

    const explicitSponsorIds = validIds(links.map((l) => l.sponsor_id));
    const dealIds = validIds(links.map((l) => l.deal_id));
    const sourceIds = validIds(links.map((l) => l.source_id));

    const sponsorIds = unique([...explicitSponsorIds]);

    const collectRoles = (
      field: "sponsor_id" | "entity_id" | "deal_id" | "source_id",
      id: number,
    ) => {
      const roles = Array.from(
        new Set(
          links
            .filter((l: any) => l?.[field] === id)
            .map((l: any) => String(l?.role ?? "").trim())
            .filter(Boolean),
        ),
      );
      return roles.length ? roles.join(", ") : null;
    };

    const out: Array<{
      key: string;
      type: string;
      name: string;
      href: string;
      role: string | null;
    }> = [];

    sponsorIds.forEach((sponsorId) => {
      const name =
        sponsors.find((s: Sponsor) => s.id === sponsorId)?.sponsor_name ??
        `ID ${sponsorId}`;
      out.push({
        key: `sponsor-${sponsorId}`,
        type: "Sponsor",
        name,
        href: `/sponsors/${sponsorId}`,
        role: collectRoles("sponsor_id", sponsorId),
      });
    });

    unique(dealIds).forEach((dealId) => {
      const name =
        deals.find((d: any) => d.id === dealId)?.deal_name ?? `ID ${dealId}`;
      out.push({
        key: `deal-${dealId}`,
        type: "Deal",
        name,
        href: `/deals/${dealId}`,
        role: collectRoles("deal_id", dealId),
      });
    });

    unique(sourceIds).forEach((sourceId) => {
      const name =
        sources.find((s: any) => s.id === sourceId)?.source_name ??
        `ID ${sourceId}`;
      out.push({
        key: `source-${sourceId}`,
        type: "Source",
        name,
        href: `/sources/${sourceId}`,
        role: collectRoles("source_id", sourceId),
      });
    });

    return out;
  }, [contact.links, sponsors, deals, sources]);

  return (
    <Box
      sx={{
        display: "flex",
        gap: "10px",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "row" }}>
        {isEdit ? (
          <Box sx={{ width: "75%", display: "flex", gap: 1 }}>
            <Typography
              className="text-general-page-header-prefix"
              sx={{ mb: "0 !important", paddingBottom: "0 !important" }}
            >
              CONTACT:
            </Typography>
            <Typography
              className="text-general-page-header"
              sx={{ mb: "0 !important", paddingBottom: 0 }}
            >
              {contact.contact_name}
            </Typography>
          </Box>
        ) : null}
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <InfoSection
            title="Contact Details"
            items={[
              {
                label: "Name",
                value: contact.contact_name,
                link: `/contacts/${contact.id}`,
              },
              { label: "Company", value: contact.company ?? "-" },
              { label: "Phone", value: contact.contact_phone },
              { label: "Email", value: contact.contact_email },
              { label: "Title", value: contact.contact_title },
              { label: "Location", value: contact.contact_location },
              { label: "Notes", value: contact.notes ?? "-" },
            ]}
          />
        </Box>
        {isDealPage ? null : (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              background: "#fff",
              p: 2,
              borderRadius: 2,
              mb: 1,
              boxShadow: 1,
            }}
          >
            <Typography className="text-general-page-subheader">
              Contact Links
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {resolvedLinks.length ? (
                resolvedLinks.map((l) => (
                  <Box
                    key={l.key}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 2,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 500,
                        paddingRight: 2,
                        flex: "0 0 auto",
                        maxWidth: "60%",
                      }}
                    >
                      {l.type}
                    </Typography>
                    <Typography
                      sx={{
                        textAlign: "right",
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        flex: "1 1 auto",
                        minWidth: 0,
                      }}
                    >
                      <a
                        href={l.href}
                        style={{
                          color: "#1B7A63",
                          textDecoration: "underline",
                        }}
                      >
                        {l.name}
                      </a>
                      {l.role ? <span>{` (${l.role})`}</span> : null}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography sx={{ textAlign: "right" }}>-</Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
